import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import type { UnitProps } from './unit';

type SeparatorStyles = 'none' | 'colon' | 'dot' | 'slash' | 'line';

export type SeparatorProps = {
  size: number;
  style: SeparatorStyles;
  color: string;
  unitStyle: UnitProps['unitStyle'];
  unitFormat: string;
  showLabels: boolean;
};

export function Separator({
  size,
  style,
  color,
  unitStyle,
  unitFormat,
  showLabels
}: SeparatorProps) {
  const isColonAfterDaysUnit = style === 'colon' && unitFormat === 'days';

  return (
    <Container
      _size={size}
      _style={style}
      _unitStyle={unitStyle}
      _showLabels={showLabels}
    >
      <Show when={style === 'colon'}>
        <Show when={!isColonAfterDaysUnit} fallback={<BlankColon />}>
          <Colon>
            <Dot _color={color} />
            <Dot _color={color} />
          </Colon>
        </Show>
      </Show>

      <Show when={style === 'dot'}>
        <Dot _color={color} />
      </Show>

      <Show when={style === 'slash'}>
        <Slash _color={color}>/</Slash>
      </Show>

      <Show when={style === 'line'}>
        <Line _color={color} />
      </Show>
    </Container>
  );
}

const Container = styled.div.attrs<{
  _size: number;
  _style: SeparatorStyles;
  _unitStyle: UnitProps['unitStyle'];
  _showLabels: boolean;
}>({
  className: createReadableClass({
    component: 'separator',
    element: 'container'
  })
})`
  display: flex;
  align-items: center;
  font-size: ${({ _size }) => _size}px;
  ${({ _unitStyle }) => _unitStyle !== 'clean' && `margin: 0 0.15625em;`}
  height: 1.3125em;
  ${({ _showLabels, _style }) =>
    _showLabels &&
    (_style === 'colon' || _style === 'dot') &&
    `padding: 2px 0px;`}
`;

const BlankColon = styled.div.attrs({
  className: createReadableClass({
    component: 'separator',
    element: 'blank-colon'
  })
})`
  width: 0.15625em;
`;

const Colon = styled.div.attrs({
  className: createReadableClass({
    component: 'separator',
    element: 'colon'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 0.21875em;
`;

const Dot = styled.div.attrs<{ _color: string }>({
  className: createReadableClass({
    component: 'separator',
    element: 'dot'
  })
})`
  border-radius: 50%;
  width: 0.15625em;
  height: 0.15625em;
  background-color: ${({ _color }) => _color};
`;

const Slash = styled.div.attrs<{ _color: string }>({
  className: createReadableClass({
    component: 'separator',
    element: 'slash'
  })
})`
  width: 0.33334em;
  font-size: 1.125em;
  margin-bottom: 0.25em;
  color: ${({ _color }) => _color};
`;

const Line = styled.div.attrs<{ _color: string }>({
  className: createReadableClass({
    component: 'separator',
    element: 'line'
  })
})`
  height: 100%;
  border-left: ${({ _color }) => `0.0625em solid ${_color}`};
`;
